import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer, Download, Award, Lock } from "lucide-react";
import { previewStore } from "./previewStore";
import api from "../../lib/api";
import usePageMeta from "../../lib/usePageMeta";

// ---------------------------------------------------------------------------
// HOSP-101 Certificate of Completion
// - Admin-preview only: HOSP-101 is not published; only admins in an active
//   preview session can reach this page.
// - Two modes:
//   * ?mode=admin-preview  \u2192 renders a non-persisted PREVIEW-HOSP101 certificate
//     (never issues a real ID, never mutates progress or completion state).
//   * default              \u2192 requires admin to have passed the assessment and
//     acknowledged the reflection. Idempotently issues a stable cert ID.
// - Print + PDF supported via window.print() with print-only CSS.
// - Course footer contains the exact professional-scope disclaimer.
// ---------------------------------------------------------------------------
export default function PortalHosp101Certificate() {
  usePageMeta({ title: "HOSP-101 \u2014 Certificate of Completion | CCCHAPS Academy Preview", noindex: true });
  const nav = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const isAdminPreview = params.get("mode") === "admin-preview" || location.pathname.endsWith("/certificate-preview");
  const inPreview = previewStore.isActive() && previewStore.getCourseCode() === "HOSP-101";
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!inPreview) return;
    (async () => {
      try {
        setLoading(true);
        if (isAdminPreview) {
          const r = await api.get("/admin/portal/preview/hosp101/certificate/admin-preview");
          setCert(r.data);
        } else {
          // Try to load an existing issued certificate first (idempotent read).
          try {
            const r = await api.get("/admin/portal/preview/hosp101/certificate");
            setCert(r.data);
          } catch (e1) {
            if (e1?.response?.status === 404) {
              // Not yet issued \u2014 attempt to issue (server enforces eligibility).
              const r = await api.post("/admin/portal/preview/hosp101/certificate/issue");
              setCert(r.data);
            } else {
              throw e1;
            }
          }
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || "Certificate is not available.";
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [inPreview, isAdminPreview]);

  if (!inPreview) {
    return (
      <div className="min-h-screen bg-[#FDFCF6] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 text-slate-500 mb-4">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="font-heading text-2xl text-slate-900">Admin preview only</h1>
          <p className="mt-3 text-sm text-slate-600">HOSP-101 certificates are available only within an active admin preview session.</p>
          <Link to="/care-portal/admin/academy-portal" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#4A6FA5] hover:underline">
            Return to Admin Portal
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-screen bg-[#FDFCF6] p-12 text-center text-slate-500">Loading certificate\u2026</div>;
  }

  if (error || !cert) {
    return (
      <div className="min-h-screen bg-[#FDFCF6] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-700 mb-4">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="font-heading text-2xl text-slate-900">Certificate not yet available</h1>
          <p className="mt-3 text-sm text-slate-600">{error || "Complete the Final Competency Assessment (80%) and the Final Course Reflection to unlock your certificate."}</p>
          <button onClick={() => nav("/academy/portal/hosp-101/learn")}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#4A6FA5] hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to HOSP-101
          </button>
        </div>
      </div>
    );
  }

  const completionDate = new Date(cert.completion_date || cert.issued_at);
  const dateStr = completionDate.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-[#F5F3EE]">
      {/* Non-printing controls */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 print:hidden" data-testid="hosp101-cert-controls">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button onClick={() => nav("/academy/portal/hosp-101/learn")}
                  data-testid="hosp101-cert-back"
                  className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#4A6FA5]">
            <ArrowLeft className="w-4 h-4" /> Back to Course
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => window.print()}
                    data-testid="hosp101-cert-print"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:border-[#4A6FA5]">
              <Printer className="w-4 h-4" /> Print Certificate
            </button>
            <button onClick={() => window.print()}
                    data-testid="hosp101-cert-download"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]">
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>
        </div>
        {isAdminPreview && (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="hosp101-cert-admin-preview-banner">
            <span className="font-semibold uppercase tracking-[0.14em] text-[11px]">Admin Preview</span> {"\u00b7"} This is a non-persisted preview of the certificate. No real certificate ID has been issued and learner progress has not been altered.
          </div>
        )}
      </div>

      {/* Printable certificate */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
        <div className="cert-page bg-white border border-slate-200 rounded-2xl shadow-sm mx-auto px-8 sm:px-14 py-12 sm:py-16 text-center" data-testid="hosp101-cert-page">
          {isAdminPreview && (
            <div className="cert-watermark" aria-hidden>PREVIEW</div>
          )}
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#EEF3FA] text-[#4A6FA5] mb-6">
            <Award className="w-7 h-7" />
          </div>
          <p className="text-[11px] uppercase tracking-[0.32em] text-slate-500 font-semibold">Certificate of Completion</p>
          <p className="mt-3 font-heading text-sm sm:text-base text-slate-700 italic">California Corporate Chaplains Academy</p>
          <p className="mt-10 text-sm text-slate-600">This certifies that</p>
          <p className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-slate-900" data-testid="hosp101-cert-name">
            {cert.learner_name}
          </p>
          <p className="mt-6 text-sm text-slate-600">has successfully completed</p>
          <p className="mt-3 font-heading text-2xl sm:text-3xl text-[#3B597B]">HOSP-101</p>
          <p className="mt-1 font-heading text-lg sm:text-xl text-slate-900">Practicing From Diagnosis to Goodbye</p>
          <p className="mt-2 text-sm text-slate-600 italic">Applied Patient-Centered Chaplaincy Through the End-of-Life Journey</p>

          <p className="mx-auto mt-8 max-w-2xl text-sm text-slate-700 leading-relaxed">
            A book-based specialty course in patient-centered hospice chaplaincy covering spiritual assessment, presence, communication, interdisciplinary collaboration, documentation, end-of-life accompaniment, and bereavement support.
          </p>

          <div className="mt-10 flex flex-wrap items-end justify-center gap-x-16 gap-y-6">
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Completion Date</p>
              <p className="mt-1 font-heading text-slate-900" data-testid="hosp101-cert-date">{dateStr}</p>
            </div>
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Certificate ID</p>
              <p className="mt-1 font-heading text-slate-900" data-testid="hosp101-cert-id">{cert.cert_id}</p>
            </div>
          </div>

          <div className="mt-12 mx-auto max-w-md">
            <div className="border-t-2 border-slate-800 pt-3">
              <p className="font-heading text-lg text-slate-900">Chaplain Derrick Hall</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mt-1">Course Author / Instructor</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">California Corporate Chaplains Academy</p>
            </div>
          </div>

          <p className="mt-10 font-heading italic text-slate-700">See the Person. Hear the Voice. Honor the Journey.</p>

          <p className="mt-10 text-[11px] text-slate-500 leading-relaxed italic max-w-3xl mx-auto">
            This certificate documents successful completion of HOSP-101 educational training and does not represent professional licensure or board certification.
          </p>
        </div>
      </div>

      {/* Print styles \u2014 forces the certificate onto ONE 8.5 x 11 landscape page.
          Preserves all approved wording; only spacing/typography scale in print. */}
      <style>{`
        @media print {
          @page { size: letter landscape; margin: 0.3in; }
          html, body {
            background: #ffffff !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .print\\:hidden { display: none !important; }
          .cert-page {
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            page-break-inside: avoid !important;
            page-break-before: auto !important;
            page-break-after: avoid !important;
            padding: 0.35in 0.55in !important;
            max-width: 100% !important;
            width: 100% !important;
            height: auto !important;
            max-height: 7.9in !important;
            overflow: hidden !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
          }
          /* Tighter vertical rhythm so all approved copy fits on one page. */
          .cert-page * { line-height: 1.25 !important; }
          .cert-page .mb-6 { margin-bottom: 0.35rem !important; }
          .cert-page .mt-1 { margin-top: 0.1rem !important; }
          .cert-page .mt-2 { margin-top: 0.15rem !important; }
          .cert-page .mt-3 { margin-top: 0.25rem !important; }
          .cert-page .mt-6 { margin-top: 0.35rem !important; }
          .cert-page .mt-8 { margin-top: 0.45rem !important; }
          .cert-page .mt-10 { margin-top: 0.5rem !important; }
          .cert-page .mt-12 { margin-top: 0.55rem !important; }
          .cert-page .pt-3 { padding-top: 0.2rem !important; }
          .cert-page .gap-x-16 { column-gap: 3rem !important; }
          .cert-page .gap-y-6 { row-gap: 0.4rem !important; }
          /* Shrink the round icon so it doesn't push everything down. */
          .cert-page .w-14 { width: 2rem !important; }
          .cert-page .h-14 { height: 2rem !important; }
          .cert-page .w-14 svg, .cert-page .h-14 svg { width: 1rem !important; height: 1rem !important; }
          /* Right-size typography for a single landscape page. */
          .cert-page .text-\\[11px\\] { font-size: 8.5px !important; letter-spacing: 0.24em !important; }
          .cert-page .text-\\[10px\\] { font-size: 8px !important; letter-spacing: 0.2em !important; }
          .cert-page .text-sm { font-size: 0.8rem !important; }
          .cert-page .text-base { font-size: 0.85rem !important; }
          .cert-page .text-lg { font-size: 0.95rem !important; }
          .cert-page .text-xl { font-size: 1.05rem !important; }
          .cert-page .text-2xl { font-size: 1.15rem !important; }
          .cert-page .text-3xl { font-size: 1.35rem !important; }
          .cert-page .text-4xl { font-size: 1.9rem !important; }
          .cert-page .text-5xl { font-size: 2.15rem !important; }
          /* Course description block: use full width so it reflows into fewer lines. */
          .cert-page .max-w-2xl { max-width: 90% !important; }
          .cert-page .max-w-3xl { max-width: 95% !important; }
          .cert-page .max-w-md { max-width: 20rem !important; }
          /* Watermark stays visible in preview but scales for one page. */
          .cert-watermark { font-size: 6rem !important; }
        }
        .cert-page { position: relative; }
        .cert-watermark {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: inherit;
          font-weight: 800;
          font-size: 8rem;
          letter-spacing: 0.5rem;
          color: rgba(148, 163, 184, 0.18);
          transform: rotate(-24deg);
          pointer-events: none;
          user-select: none;
          z-index: 0;
        }
      `}</style>
    </div>
  );
}
